import { Divider, Stack, Typography } from '@mui/material';
import { PriceRoundingConfigsList } from '@retail/rounding/components';
import { useDisclosure, usePersistentNavigation } from '@shared/hooks';
import { ContainedButton, FlexRow } from '@shared/components';
import AddIcon from '@mui/icons-material/Add';
import { PriceRoundingRuleConfig } from '@retail/rounding/types';
import { useFetchPriceRoundingRules } from '@retail/rounding/data-access';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { Link, RetailTanstackQueryLoadingWrapper } from '@retail/components';
import React, { useState } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import CreateOrUpdateRoundingRuleDialog from './CreateOrUpdateRoundingRuleDialog';
import ViewRoundingRuleDialog from './ViewRoundingRuleDialog';
import { brandLogo } from '@retail/utils';
import { DeleteRoundingRuleDialog } from './DeleteRoundingRuleDialog';
import { mgPrisRoutes } from '@retail/mgpris/config';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';

const roundingConditionsPath = mgPrisRoutes.calculus.calculusArea.getFullLinkPath({
  calculusArea: 'rounding',
});

interface Props {
  renderConfigEntryHeading: (priceRoundingConfig: PriceRoundingRuleConfig) => React.ReactNode;
}

export const PriceRoundingContainer: React.FC<Props> = ({ renderConfigEntryHeading }) => {
  const { getPersistentSearchParams } = usePersistentNavigation();

  const { selectedBrand } = useUserBrands();
  const t = useAppTFunction();
  const { userCanCreate, userCanUpdate, userCanDelete, userCanRead } =
    usePermissionsForCurrentUser();
  const canCreateRoundingRules = userCanCreate('ROUNDING');
  const canUpdateRoundingRules = userCanUpdate('ROUNDING');
  const canDeleteRoundingRules = userCanDelete('ROUNDING');
  const canViewRoundingConditionss = userCanRead('PRICE_CALCULATION__ROUNDING');
  const createRoundingRuleDialog = useDisclosure();
  const [roundingConfigToCopy, setRoundingConfigToCopy] = useState<PriceRoundingRuleConfig>();
  const [roundingConfigToUpdate, setRoundingConfigToUpdate] = useState<PriceRoundingRuleConfig>();
  const [roundingConfigToDelete, setRoundingConfigToDelete] = useState<PriceRoundingRuleConfig>();
  const [roundingRuleDialogToView, setRoundingRuleDialogToView] = useState<
    PriceRoundingRuleConfig | undefined
  >(undefined);

  const { assortmentId, orgUnitType } = useSelectedOrgUnit();

  const onSetRoundingConfigToCopy = canCreateRoundingRules ? setRoundingConfigToCopy : undefined;

  const onSetRoundingConfigToUpdate = canUpdateRoundingRules
    ? setRoundingConfigToUpdate
    : undefined;

  const onSetRoundingConfigToDelete = canDeleteRoundingRules
    ? setRoundingConfigToDelete
    : undefined;

  const loadingState = useFetchPriceRoundingRules({ assortmentId, suspense: false });

  return (
    <Stack flexGrow={1} overflow="hidden">
      <FlexRow justifyContent="space-between" p={3}>
        <Typography variant="h4" component="h1">
          {t('rounding.title')}
        </Typography>

        <Stack direction="row" gap={2}>
          {canViewRoundingConditionss && (
            <Link
              to={{
                pathname: roundingConditionsPath,
                search: getPersistentSearchParams(roundingConditionsPath),
              }}
            >
              {t('rounding.actions.roundingConditionsLink')}
              <ArrowRightAltRoundedIcon />
            </Link>
          )}

          {canCreateRoundingRules && (
            <ContainedButton
              startIcon={<AddIcon fontSize="small" />}
              size="small"
              color="secondary"
              onClick={createRoundingRuleDialog.onToggle}
            >
              {t('rounding.actions.newRule')}
            </ContainedButton>
          )}
        </Stack>
      </FlexRow>

      <Divider />

      <Stack flexGrow={1} overflow="auto">
        <RetailTanstackQueryLoadingWrapper loadingState={loadingState} showReloadButtonOnError>
          {(priceRoundingRuleConfigs) => (
            <PriceRoundingConfigsList
              priceRoundingConfigs={priceRoundingRuleConfigs.map((dto) => {
                const isSameOrgLevel =
                  (dto.type === 'MEMBER' && orgUnitType.level === 'LOCATION') ||
                  (dto.type === 'BRAND' && orgUnitType.level === 'BRAND');

                return PriceRoundingRuleConfig.fromDto(
                  dto,
                  {
                    name: selectedBrand.name,
                    id: selectedBrand.brandId,
                    imgUrl: brandLogo(selectedBrand.brandId).logo,
                  },
                  isSameOrgLevel ? ['UPDATE', 'DELETE'] : []
                );
              })}
              onOpenView={setRoundingRuleDialogToView}
              onCopy={onSetRoundingConfigToCopy}
              onEdit={onSetRoundingConfigToUpdate}
              onDelete={onSetRoundingConfigToDelete}
              renderHeading={renderConfigEntryHeading}
            />
          )}
        </RetailTanstackQueryLoadingWrapper>
      </Stack>
      {createRoundingRuleDialog.isOpen && (
        <CreateOrUpdateRoundingRuleDialog
          open
          onClose={createRoundingRuleDialog.onClose}
          mode="CREATE"
        />
      )}
      {roundingConfigToCopy && (
        <CreateOrUpdateRoundingRuleDialog
          open
          onClose={() => setRoundingConfigToCopy(undefined)}
          mode="CREATE"
          ruleConfig={roundingConfigToCopy}
        />
      )}
      {roundingConfigToUpdate && (
        <CreateOrUpdateRoundingRuleDialog
          open
          onClose={() => setRoundingConfigToUpdate(undefined)}
          mode="UPDATE"
          ruleConfig={roundingConfigToUpdate}
        />
      )}
      {roundingRuleDialogToView && (
        <ViewRoundingRuleDialog
          priceRoundingRuleConfig={roundingRuleDialogToView}
          open={!!roundingRuleDialogToView}
          onClose={() => setRoundingRuleDialogToView(undefined)}
          renderHeader={() => renderConfigEntryHeading(roundingRuleDialogToView)}
        />
      )}
      {roundingConfigToDelete && (
        <DeleteRoundingRuleDialog
          roundingRule={roundingConfigToDelete}
          onClose={() => setRoundingConfigToDelete(undefined)}
        />
      )}
    </Stack>
  );
};
